import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PageData } from './data-access-page.model';
import { PageDownloadService } from '../service/download-page.service';
import { selectCurrentPages } from './data-access-page.selectors';
import * as PageActions from './data-access-page.actions';
import { withLatestFrom, tap, map, catchError, switchMap } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';

@Injectable()
export class PageEffects {
  private actions$ = inject(Actions);
  private downloadPageService = inject(PageDownloadService);
  private store = inject<Store<PageData>>(Store);

  downloadCurrentPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PageActions.downloadCurrentPagesClicked, PageActions.downloadChapterClicked),
      map(() => PageActions.downloadPageStarted()),
    ),
  );

  downloadPages$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PageActions.downloadPageStarted),
        withLatestFrom(
          this.store.pipe(select(selectCurrentPages)),
          this.actions$.pipe(ofType(PageActions.downloadCurrentPagesClicked, PageActions.downloadChapterClicked)),
        ),
        switchMap(([, pages, action]) => {
          let pageStart = 0,
            pageEnd = 0;

          // Check if this is a chapter download
          if (action.type === PageActions.downloadChapterClicked.type) {
            pageStart = (action as ReturnType<typeof PageActions.downloadChapterClicked>).startPage;
            pageEnd = (action as ReturnType<typeof PageActions.downloadChapterClicked>).endPage;
          } else {
            // Handle page download
            const currentPage = (pages as PageData[]).find((page) => page.type === 'current');

            if (currentPage) {
              /**
               *  On small viewports only one page can be seen at a time. In that cases we can only download this current page
               */
              pageStart = currentPage.index + 1;
              pageEnd = currentPage.index + 1;
            } else {
              const pageLeft = (pages as PageData[]).find((page) => page.type === 'left');
              const pageRight = (pages as PageData[]).find((page) => page.type === 'right');
              const pageAction = action as ReturnType<typeof PageActions.downloadCurrentPagesClicked>;

              if (pageAction.pageType === 'both') {
                pageStart = pageLeft?.index ?? pageRight?.index ?? 0;
                pageEnd = pageRight?.index ?? pageLeft?.index ?? 0;
              } else if (pageAction.pageType === 'left') {
                pageStart = pageLeft?.index ?? 0;
                pageEnd = pageStart;
              } else if (pageAction.pageType === 'right') {
                pageStart = pageRight?.index ?? 0;
                pageEnd = pageStart;
              }
              pageStart += 1;
              pageEnd += 1;
            }
          }

          return this.downloadPageService
            .downloadRange(pageStart, pageEnd, action.categoryId, action.documentId, action.docuLanguage)
            .pipe(
              tap((blob) => {
                const filename = `catalog-pages-${pageStart}-${pageEnd}.pdf`;
                this.downloadPageService.downloadBlob(blob, filename);
              }),
              map(() => PageActions.downloadPageCompleted()),
              catchError(() => of(PageActions.downloadPageFailed())),
            );
        }),
      ),
    { dispatch: true },
  );
}
